// WlcPhoneNumberField
// --------------------
// Self-contained phone number input with country-code selector for the WLC
// screener.  Normalises to E.164 on blur and on every keystroke so the parent
// form always has a validated value available.
//
// Adapted from the RMS PhoneNumberField with WLC branding and an inlined
// country list (no cross-product imports).

import React, { useState, useMemo, useRef, useEffect } from 'react';
import { PHONE_COUNTRIES, DEFAULT_PHONE_COUNTRY } from '../constants/options';

const BRAND_ACCENT = '#e7c22f';
const STORAGE_KEY = 'wlc_phone_country';

function digitsOnly(s) { return (s || '').replace(/\D/g, ''); }

/**
 * Derive an E.164-ish number from a loosely-formatted user input.
 * Handles inputs that already start with '+', inputs starting with the
 * national trunk prefix (e.g. '0' in the UK), and bare local numbers.
 */
function deriveE164(raw, dialCode, trunkPrefix) {
    const cleaned = String(raw || '').replace(/[()\s-]/g, '');
    if (!dialCode) return '';
    const dialDigits = digitsOnly(dialCode);
    if (cleaned.startsWith('+')) return '+' + digitsOnly(cleaned);
    const num = digitsOnly(cleaned);
    if (!num) return '';
    if (num.startsWith(dialDigits)) return '+' + num;
    if (trunkPrefix && num.startsWith(trunkPrefix)) return dialCode + num.slice(trunkPrefix.length);
    return dialCode + num;
}

export default function WlcPhoneNumberField({ rawValue, onRawChange, onNormalizedChange, label = 'Phone number', required = false }) {
    const initialCode = useMemo(() => {
        if (typeof window !== 'undefined') {
            const stored = window.localStorage.getItem(STORAGE_KEY);
            if (stored) return stored;
        }
        return DEFAULT_PHONE_COUNTRY;
    }, []);

    const [countryCode, setCountryCode] = useState(initialCode);
    const country = useMemo(() => PHONE_COUNTRIES.find(c => c.code === countryCode) || PHONE_COUNTRIES[0], [countryCode]);
    const [hint, setHint] = useState('');
    const [showHint, setShowHint] = useState(false);
    const liveRef = useRef(null);

    // Persist selected country to localStorage
    useEffect(() => {
        try { window.localStorage.setItem(STORAGE_KEY, countryCode); } catch (_) { /* noop */ }
    }, [countryCode]);

    const handleRawChange = (e) => {
        const val = e.target.value.replace(/[^0-9()\s+-]/g, '');
        onRawChange?.(val);
        const e164 = deriveE164(val, country.dialCode, country.trunkPrefix);
        const len = digitsOnly(e164).length;
        const ok = len >= 10 && len <= 20;
        onNormalizedChange?.(ok ? e164 : '');
        if (ok) { setHint(''); setShowHint(false); }
    };

    const handleBlur = () => {
        const e164 = deriveE164(rawValue, country.dialCode, country.trunkPrefix);
        onNormalizedChange?.(e164);
        const len = digitsOnly(e164).length;
        const ok = len >= 10 && len <= 20;
        setHint(ok ? '' : 'Please enter a valid phone number.');
        setShowHint(!!rawValue && !ok);
    };

    return (
        <div>
            {label && (
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    {label}{required && <span className="text-red-600"> *</span>}
                </label>
            )}
            <div className="flex items-center gap-2">
                {/* Country code selector */}
                <div className="relative shrink-0">
                    <select
                        value={countryCode}
                        onChange={(e) => setCountryCode(e.target.value)}
                        className="appearance-none pr-8 p-3 border-2 border-gray-200 rounded-xl bg-white text-gray-800 text-base focus:outline-none focus:ring-2 focus:border-transparent"
                        style={{ '--tw-ring-color': BRAND_ACCENT }}
                        aria-label="Country code"
                    >
                        {PHONE_COUNTRIES.map(c => (
                            <option key={c.code} value={c.code}>{c.dialCode}</option>
                        ))}
                    </select>
                    <svg className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" viewBox="0 0 20 20" aria-hidden="true">
                        <path d="M5.5 7.5l4.5 4.5 4.5-4.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </div>

                {/* Phone input */}
                <input
                    type="tel"
                    inputMode="tel"
                    value={rawValue || ''}
                    onChange={handleRawChange}
                    onBlur={handleBlur}
                    placeholder="e.g. 07123 456 789"
                    className="flex-1 min-w-0 p-3 border-2 border-gray-200 rounded-xl bg-white text-gray-900 text-base placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent"
                    style={{ '--tw-ring-color': BRAND_ACCENT }}
                />
            </div>

            {/* Accessibility live region */}
            <div className="sr-only" aria-live="polite" aria-atomic="true" ref={liveRef} />

            {/* Validation hint */}
            <div className={`transition-all duration-300 overflow-hidden ${showHint ? 'max-h-6 mt-1' : 'max-h-0 mt-0'}`}>
                <div className={`text-xs text-red-600 transition-opacity duration-300 ${showHint ? 'opacity-100' : 'opacity-0'}`}>
                    {hint}
                </div>
            </div>
        </div>
    );
}

export { deriveE164 };
